import { useCallback, useState } from 'react';
import {
  faCalendarXmark,
  faEllipsis,
  faEye,
  faEyeSlash,
  faFolderArrowRight,
  faGear,
  faPen,
  faThumbTack,
  faTrashCan,
} from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import { Link, usePage } from '@inertiajs/react';

import { DeletePageDialog, type DeletePageTarget } from '@/components/shared/delete-page-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Icon } from '@/components/ui/icon';
import { SidebarMenuAction } from '@/components/ui/sidebar';
import { type PageScheduleState, pageScheduleState } from '@/lib/page-scheduling';
import { getPageDisplayTitle } from '@/lib/page-utils';
import { cmsPagesEditorPath } from '@/lib/routes';
import { cn } from '@/lib/utils';
import { usePageEditorStore } from '@/stores/page-editor-store';
import { usePagesActions } from '@/stores/pages-store';
import { CHILD_PAGE_TYPES, type Page, PageType } from '@/types/page';
import type { SharedData } from '@/types/shared';
import { PageVisibilityWarningDialog } from './page-visibility-warning';
import { usePageVisibilityDropdownWarnings } from './use-page-visibility-dropdown-warnings';

interface PageDropdownMenuProps {
  page: Page;
  showOnHover?: boolean;
  className?: string;
  isNested?: boolean;
  canPin?: boolean;
}

const ellipsisIcon = <Icon icon={faEllipsis} />;

type SidebarPublishMenuPage = Pick<Page, 'id' | 'isPublished' | 'pageType'> | null;

export function sidebarPublishMenuState({
  page,
  scheduleState,
}: {
  page: SidebarPublishMenuPage;
  scheduleState: Pick<PageScheduleState, 'isScheduleMissed' | 'isScheduled'>;
}) {
  const isHomePage = page?.pageType === PageType.HOME;
  const isPublished = Boolean(page?.isPublished);

  return {
    canCancelSchedule: Boolean(
      page?.pageType === PageType.BLOG &&
        !isPublished &&
        (scheduleState.isScheduled || scheduleState.isScheduleMissed),
    ),
    isPublishActionUnavailable: Boolean(!page?.id || (isPublished && isHomePage)),
    publishActionLabel: isPublished ? 'Unpublish' : 'Publish',
  };
}

export function PageDropdownMenu({
  page,
  showOnHover = true,
  className,
  isNested = false,
  canPin = true,
}: PageDropdownMenuProps) {
  const { site } = usePage<SharedData>().props;
  const editorPage = usePageEditorStore((state) => state.page);
  const {
    publishPage,
    unschedulePage,
    unpublishPage,
    movePageToNonNavigation,
    movePageToMainNavigation,
    togglePin,
  } = usePagesActions();
  const isPublished = page.isPublished;
  const isNav = page.isNav;
  const isHomePage = page.pageType === PageType.HOME;
  const editorPageMatchesDropdownPage = editorPage?.id === page.id;
  const pageForSchedule = editorPageMatchesDropdownPage ? editorPage : page;
  const scheduleState = pageScheduleState(pageForSchedule, site?.timeZoneIdentifier);
  const { canCancelSchedule, isPublishActionUnavailable, publishActionLabel } =
    sidebarPublishMenuState({
      page: pageForSchedule,
      scheduleState,
    });
  const settingsUrl = page.id ? `${cmsPagesEditorPath(page.id)}?settings=true` : undefined;
  const [pageToDelete, setPageToDelete] = useState<DeletePageTarget | null>(null);

  // Child page types (blog, case_result) cannot be moved
  // as they always belong under their respective index pages
  const isChildPageType =
    page.pageType && (CHILD_PAGE_TYPES as readonly string[]).includes(page.pageType);
  const isPinnable = isChildPageType;

  const handleMoveToNonNavigation = useCallback(async () => {
    if (!page.id) return;

    await movePageToNonNavigation(page.id);
  }, [page.id, movePageToNonNavigation]);

  const handleMoveToMainNav = useCallback(async () => {
    if (!page.id) return;

    await movePageToMainNavigation(page.id);
  }, [page.id, movePageToMainNavigation]);

  const handlePublish = useCallback(async () => {
    if (!page.id) return;

    await publishPage(page.id);
  }, [page.id, publishPage]);

  const handleUnpublish = useCallback(async () => {
    if (!page.id) return;

    await unpublishPage(page.id);
  }, [page.id, unpublishPage]);

  const handleCancelSchedule = useCallback(async () => {
    if (!page.id) return;

    await unschedulePage(page.id);
  }, [page.id, unschedulePage]);

  const { warning, setWarning, requestPublish, requestUnpublish, confirmWarning } =
    usePageVisibilityDropdownWarnings(page, {
      onPublish: handlePublish,
      onUnpublish: handleUnpublish,
    });

  const handlePublishAction = useCallback(async () => {
    if (!page.id || isPublishActionUnavailable) return;

    if (isPublished) {
      requestUnpublish();
      return;
    }

    requestPublish();
  }, [isPublishActionUnavailable, isPublished, page.id, requestPublish, requestUnpublish]);

  const handlePublishToggle = useCallback(() => {
    void handlePublishAction();
  }, [handlePublishAction]);

  const handleDelete = useCallback(() => {
    if (!page?.id) return;
    setPageToDelete({ id: page.id, title: getPageDisplayTitle(page) });
  }, [page]);

  const handleMove = useCallback(() => {
    if (isNav) {
      handleMoveToNonNavigation();
    } else {
      handleMoveToMainNav();
    }
  }, [isNav, handleMoveToNonNavigation, handleMoveToMainNav]);

  const handleTogglePin = useCallback(async () => {
    if (!page.id) return;

    await togglePin(page.id, page.pinned);
  }, [page.id, page.pinned, togglePin]);

  const dropdownMenuItems = (
    <>
      <DropdownMenuItem asChild disabled={!page.id}>
        <Link href={settingsUrl} preserveScroll>
          <Icon icon={faPen} size="sm" className="mr-2" />
          Rename
        </Link>
      </DropdownMenuItem>

      <DropdownMenuItem onSelect={handlePublishToggle} disabled={isPublishActionUnavailable}>
        <Icon icon={isPublished ? faEyeSlash : faEye} size="sm" className="mr-2" />
        {publishActionLabel}
      </DropdownMenuItem>

      {canCancelSchedule && (
        <DropdownMenuItem onSelect={handleCancelSchedule}>
          <Icon icon={faCalendarXmark} size="sm" className="mr-2" />
          Cancel Schedule
        </DropdownMenuItem>
      )}

      {isPinnable && (
        <DropdownMenuItem onSelect={handleTogglePin} disabled={!page.pinned && !canPin}>
          <Icon icon={faThumbTack} size="sm" className="mr-2" />
          {page.pinned ? 'Unpin' : 'Pin'}
        </DropdownMenuItem>
      )}

      {!isChildPageType && (
        <DropdownMenuItem onSelect={handleMove} disabled={isHomePage}>
          <Icon icon={faFolderArrowRight} size="sm" className="mr-2" />
          {isNav ? 'Move to Non-Navigation Pages' : 'Move to Main Navigation'}
        </DropdownMenuItem>
      )}

      <DropdownMenuItem asChild disabled={!page.id}>
        <Link href={settingsUrl} preserveScroll>
          <Icon icon={faGear} size="sm" className="mr-2" />
          Page Settings
        </Link>
      </DropdownMenuItem>

      <DropdownMenuItem onSelect={handleDelete} variant="destructive" disabled={isHomePage}>
        <Icon icon={faTrashCan} size="sm" className="mr-2" />
        Delete
      </DropdownMenuItem>
    </>
  );

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuAction
            showOnHover={showOnHover}
            aria-label={`More options for ${page.title}`}
            className={cn(
              'md:after:block',
              isNested &&
                'group-hover/menu-item:opacity-0 group-hover/nested-item:opacity-100 group-focus-within/nested-item:opacity-100',
              className,
            )}
          >
            {ellipsisIcon}
          </SidebarMenuAction>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">{dropdownMenuItems}</DropdownMenuContent>
      </DropdownMenu>
      <DeletePageDialog
        page={pageToDelete}
        open={!!pageToDelete}
        onOpenChange={(open) => !open && setPageToDelete(null)}
      />
      <PageVisibilityWarningDialog
        open={!!warning}
        onOpenChange={(open) => {
          if (!open) setWarning(null);
        }}
        {...(warning?.type === 'unpublish'
          ? { variant: 'unpublish', subpageCount: warning.subpageCount }
          : { variant: warning?.type ?? 'publish' })}
        onConfirm={confirmWarning}
        onCancel={() => setWarning(null)}
        stopPointerDownPropagation
      />
    </>
  );
}
